import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { type Stripe, loadStripe } from "@stripe/stripe-js"
import { type FormEvent, useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

// Stripe.js requires one loader per {publishableKey, stripeAccount} pair;
// cache so reopening the modal doesn't re-download the bundle.
const stripeCache = new Map<string, Promise<Stripe | null>>()
function getStripe(publishableKey: string, stripeAccount: string) {
  const key = `${publishableKey}::${stripeAccount}`
  let promise = stripeCache.get(key)
  if (!promise) {
    promise = loadStripe(publishableKey, { stripeAccount })
    stripeCache.set(key, promise)
  }
  return promise
}

export interface IntentPayload {
  client_secret: string
  publishable_key: string
  connected_account_id: string
  amount_cents?: number
}

// A secondary action rendered to the left of the form's Cancel button — e.g.
// "Use the card on file instead" when the embedded form was revealed from a
// one-click confirmation.
export interface SecondaryAction {
  label: string
  onClick: () => void
}

// The embedded card form on its own — fetches a fresh intent when `active`,
// mounts Stripe against the connected account, and renders the PaymentElement +
// footer. Used both by the standalone modal and inside the agent-purchase
// confirmation (IMP-389), so the confirm logic lives in one place.
export function EmbeddedPaymentForm({
  active,
  mode,
  submitLabel,
  fetchIntent,
  onConfirmed,
  onCancel,
  secondary,
}: {
  active: boolean
  mode: "payment" | "setup"
  submitLabel: string
  fetchIntent: () => Promise<IntentPayload>
  onConfirmed: (intentId: string) => Promise<void> | void
  onCancel: () => void
  secondary?: SecondaryAction
}) {
  const [intent, setIntent] = useState<IntentPayload | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!active) {
      setIntent(null)
      setLoadError(null)
      return
    }
    let cancelled = false
    fetchIntent()
      .then((payload) => {
        if (!cancelled) setIntent(payload)
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setLoadError(
            err instanceof Error ? err.message : "Couldn't start payment.",
          )
      })
    return () => {
      cancelled = true
    }
    // fetchIntent is stable for a given active cycle; re-run only on active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  const stripePromise = useMemo(
    () =>
      intent
        ? getStripe(intent.publishable_key, intent.connected_account_id)
        : null,
    [intent],
  )

  if (loadError) return <p className="text-destructive text-sm">{loadError}</p>
  if (!intent || !stripePromise)
    return (
      <p className="text-muted-foreground py-6 text-center text-sm">
        Loading secure payment form…
      </p>
    )

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: intent.client_secret,
        appearance: { theme: "stripe" },
      }}
    >
      <PaymentForm
        mode={mode}
        submitLabel={submitLabel}
        onConfirmed={onConfirmed}
        onCancel={onCancel}
        secondary={secondary}
      />
    </Elements>
  )
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  // "payment" confirms a PaymentIntent; "setup" confirms a SetupIntent.
  mode: "payment" | "setup"
  submitLabel: string
  // Fetches a fresh client secret from the server when the modal opens.
  fetchIntent: () => Promise<IntentPayload>
  // Called with the confirmed intent id; should perform the server-side
  // fulfillment and (for payment) navigate away.
  onConfirmed: (intentId: string) => Promise<void> | void
}

// Reusable embedded-Elements modal (IMP-195). Used to update the saved card.
// Keeps everything white-labeled — it's our own UI mounted against the
// partner's connected account.
export function StripePaymentModal({
  open,
  onOpenChange,
  title,
  description,
  mode,
  submitLabel,
  fetchIntent,
  onConfirmed,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <EmbeddedPaymentForm
          active={open}
          mode={mode}
          submitLabel={submitLabel}
          fetchIntent={fetchIntent}
          onConfirmed={onConfirmed}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

function PaymentForm({
  mode,
  submitLabel,
  onConfirmed,
  onCancel,
  secondary,
}: {
  mode: "payment" | "setup"
  submitLabel: string
  onConfirmed: (intentId: string) => Promise<void> | void
  onCancel: () => void
  secondary?: SecondaryAction
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return

    setError(null)
    setSubmitting(true)
    try {
      const confirmParams = { return_url: window.location.href }
      const result =
        mode === "payment"
          ? await stripe.confirmPayment({
              elements,
              confirmParams,
              redirect: "if_required",
            })
          : await stripe.confirmSetup({
              elements,
              confirmParams,
              redirect: "if_required",
            })

      if (result.error) {
        setError(result.error.message ?? "Payment could not be confirmed.")
        return
      }

      const intent =
        "paymentIntent" in result ? result.paymentIntent : result.setupIntent
      if (!intent) {
        setError("Payment could not be confirmed.")
        return
      }

      // Keep the spinner up while the server fulfills + navigates.
      await onConfirmed(intent.id)
      // Auto-close on success. For payment mode onConfirmed has already
      // navigated away, so this is a no-op there; for setup (update card) it's
      // what dismisses the modal once the new card is saved.
      onCancel()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)}>
      <div className="mt-2 space-y-4">
        {/* IMP-389: suppress the Link overlay inside the card form, matching
            the landing checkout. The tabs themselves are pinned by the
            intent's payment_method_types (card + US bank account). */}
        <PaymentElement
          options={{ layout: "tabs", wallets: { link: "never" } }}
        />
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
      <DialogFooter className="mt-6 sm:justify-between">
        {secondary ? (
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground"
            onClick={secondary.onClick}
            disabled={submitting}
          >
            {secondary.label}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!stripe || submitting}
            loading={submitting}
          >
            {submitting ? "Processing…" : submitLabel}
          </Button>
        </div>
      </DialogFooter>
    </form>
  )
}
